import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { Observable } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';
import {
  Occupation,
  OccupationEditMode,
  OccupationRatio,
} from 'src/app/@core/domain/interfaces/occupation.interface';
import { FacilitiesApiService } from 'src/app/@core/api/facilities-api.service';
import { ParkbaseStorageService } from 'src/app/@shared/services/parkbaseStorage.service';
import { TotalsHelper } from './totals.helper';
import { TrackingService } from 'src/app/@shared/services/tracking.service';
import {
  UntypedFormBuilder,
  UntypedFormControl,
  UntypedFormGroup,
  Validators,
} from '@angular/forms';
import { ConfirmDialogComponent } from './confirm-dialog/confirm-dialog.component';
import { NbDialogService, NbIconConfig, NbToastrService } from '@nebular/theme';
import {
  AdjustFacilityAction,
  FacilityActionResult,
  FacilityActionType,
  OccupationAdjustment,
  ReservationOccupationAdjustment,
} from '../../../../@core/domain/interfaces/facility.interface';
import { LogService } from '../../../../@core';
import { EChartsOption } from 'echarts';
import { formatNumber } from '@angular/common';
import { isNotNullOrUndefined } from 'codelyzer/util/isNotNullOrUndefined';

interface Choice {
  save: boolean;
  sync: boolean;
  name: string;
}

@Component({
  selector: 'app-totals',
  templateUrl: './totals.component.html',
  styleUrls: ['./totals.component.scss'],
})
export class TotalsComponent implements OnInit, OnDestroy {
  @Input()
  facilityId!: number;

  @Output()
  formSaved = new EventEmitter<boolean>();

  totals!: Observable<Occupation>;
  totalsForm!: UntypedFormGroup;
  editMode!: OccupationEditMode;
  index!: number;

  successMessage!: string;
  errorMessage!: string;

  chartOption: EChartsOption = {
    series: [{}],
  };
  initOps: object = {
    renderer: 'svg',
  };

  loaded: boolean;
  editing!: boolean;
  savingForm!: boolean;
  totalCapacity = 0;
  totalOccupation = 0;
  totalFree = 0;

  labelRatio = 0;
  label = '';
  full = false;

  private theme!: string;
  private data!: Occupation;

  private occupationAdjustments: OccupationAdjustment[] = [];
  private reservationOccupationAdjustments: ReservationOccupationAdjustment[] = [];

  constructor(
    private facilitiesApiService: FacilitiesApiService,
    private parkbaseStorageService: ParkbaseStorageService,
    private translate: TranslateService,
    private trackingService: TrackingService,
    private formBuilder: UntypedFormBuilder,
    private dialogService: NbDialogService,
    private logger: LogService,
    private toastrService: NbToastrService
  ) {
    this.loaded = false;
  }

  ngOnInit(): void {
    if (!this.facilityId) {
      return;
    }
    this.savingForm = false;
    this.totals = this.facilitiesApiService.occupationPerHourCurrent;
    this.facilitiesApiService.loadTotals(this.facilityId);
    this.theme = this.parkbaseStorageService.getTheme();
    this.resetTotalsForm();
    this.totals.subscribe((data) => {
      this.data = data;
      this.totalOccupation = 0;
      if (data && data.occupationRatios.length > 0) {
        this.setChartOptions();

        this.loaded = true;
        this.editing = false;

        this.editMode = data.occupationRatios[0].editMode;
        this.totalCapacity = data.occupationRatios[0].capacity;
        this.totalOccupation = data.occupationRatios[0].occupation;

        this.data.occupationRatios.forEach((value: OccupationRatio, i: number) => {
          if (i === 0) {
            this.labelRatio = value.ratio;
            this.full = this.labelRatio === 100;
          }
          if (i >= 1) {
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            this.chartOption.series[1].data.push({
              value: value.ratio,
              name: value.type.name,
              itemStyle: {
                color: TotalsHelper.getCategoryColor(value.type.id, value.ratio),
              },
              label: {
                show: false,
              },
              tooltip: {
                enabled: true,
                trigger: 'item',
              },
            });
          }
        });

        this.totalFree = this.totalCapacity - this.totalOccupation;

        let percentage = 0;
        if (this.totalFree > 0) {
          percentage =
            this.totalCapacity >= this.totalFree ? (this.totalFree / this.totalCapacity) * 100 : 0;
        }

        let prefixWord = '';
        let labelWord = '';

        if (this.editMode === 0) {
          switch (this.translate.currentLang) {
            case 'en-US':
            case 'en-GB':
              prefixWord = 'Total occupied \n';
              labelWord = ' of ';
              break;
            case 'nl-NL':
              prefixWord = 'Totaal bezet \n';
              labelWord = ' van ';
              break;
          }
        }

        if (this.editMode === 1) {
          switch (this.translate.currentLang) {
            case 'en-US':
            case 'en-GB':
              prefixWord = 'Total free \n';
              labelWord = ' of ';
              break;
            case 'nl-NL':
              prefixWord = 'Totaal vrij \n';
              labelWord = ' van ';
              break;
          }
        }

        let prefixNumber = this.totalOccupation;
        if (this.editMode === 1) {
          prefixNumber = this.totalCapacity - this.totalOccupation;
        }

        const centerPart = {
          value: percentage,
          name:
            prefixWord +
            formatNumber(prefixNumber, this.translate.currentLang) +
            '\n' +
            labelWord +
            formatNumber(this.totalCapacity, this.translate.currentLang),
          itemStyle: {
            color: 'transparent',
          },
          label: {
            show: true,
            fontSize: '1.25rem',
          },
        };

        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        this.chartOption.series[1].data.push(centerPart);
      }
    });
  }

  getCategoryColor(type: number, ratio: number): string {
    return TotalsHelper.getCategoryColor(type, ratio);
  }

  ngOnDestroy(): void {
    this.totalsForm = new UntypedFormGroup({});
  }

  toggleEditMode(): void {
    this.editing = !this.editing;
    this.resetTotalsForm();
    this.trackingService.eventEmitter('totalsComponent', 'action', '', 'toggleEditMode', 0);
  }

  cancelEditMode(): void {
    this.editing = false;
    this.resetTotalsForm();
    this.trackingService.eventEmitter('totalsComponent', 'action', '', 'cancelEditMode', 0);
  }

  saveTotalsForm() {
    this.occupationAdjustments = [];
    this.reservationOccupationAdjustments = [];
    this.dialogService
      .open(ConfirmDialogComponent, {
        hasBackdrop: false,
      })
      .onClose.subscribe((choice: Choice) => {
        if (choice.save) {
          this.logger.info('Saving totals form with type AdjustOccupation');
          this.submitTotalsForm(FacilityActionType.AdjustOccupation);
        }
        if (choice.sync) {
          this.logger.info('Syncing totals form with type SyncOccupationTotal');
          this.submitTotalsForm(FacilityActionType.SyncOccupationTotal);
        }
      });
  }

  private resetTotalsForm() {
    this.totalsForm = new UntypedFormGroup({});
    this.occupationAdjustments = [];
    this.reservationOccupationAdjustments = [];
    this.totals.subscribe((data) => {
      if (data) {
        data.occupationRatios.forEach((o) => {
          if (o.adjustable) {
            if (o.editMode === 0) {
              this.totalsForm.addControl(
                'ctrlOccupation' + o.type.id.toString(),
                new UntypedFormControl(o.occupation, Validators.required)
              );
            }
            if (o.editMode === 1) {
              this.totalsForm.addControl(
                'ctrlOccupation' + o.type.id.toString(),
                new UntypedFormControl(o.capacity - o.occupation, Validators.required)
              );
            }
          }
        });
        data.reservationOccupationRatios.forEach((o) => {
          if (o.adjustable) {
            this.totalsForm.addControl(
              'ctrlReservation' + o.type.id.toString(),
              new UntypedFormControl(o.occupation, Validators.required)
            );
          }
        });
      }
    });
  }

  private addToFacilityAction(type: number, value: number) {
    if (isNotNullOrUndefined(value)) {
      this.occupationAdjustments.push({ type: type, editMode: this.editMode, value: value });
    }
  }

  private addToReservationFacilityAction(type: number, value: number) {
    if (isNotNullOrUndefined(value)) {
      this.reservationOccupationAdjustments.push({ type: type, value: value });
    }
  }

  private submitTotalsForm(type: FacilityActionType): void {
    this.savingForm = true;

    this.addToFacilityAction(0, this.totalsForm.get('ctrlOccupation0')?.value);
    this.addToFacilityAction(1, this.totalsForm.get('ctrlOccupation1')?.value);
    this.addToFacilityAction(2, this.totalsForm.get('ctrlOccupation2')?.value);
    this.addToFacilityAction(3, this.totalsForm.get('ctrlOccupation3')?.value);

    this.addToReservationFacilityAction(0, this.totalsForm.get('ctrlReservation0')?.value);
    this.addToReservationFacilityAction(1, this.totalsForm.get('ctrlReservation1')?.value);
    this.addToReservationFacilityAction(2, this.totalsForm.get('ctrlReservation2')?.value);
    this.addToReservationFacilityAction(3, this.totalsForm.get('ctrlReservation3')?.value);

    const adjustFacilityAction: AdjustFacilityAction = {
      type: type,
      occupationAdjustments: this.occupationAdjustments,
      reservationOccupationAdjustments: this.reservationOccupationAdjustments,
    };

    if (this.totalsForm.valid) {
      this.facilitiesApiService
        .sendFacilityAction(this.facilityId, adjustFacilityAction)
        .subscribe((result: FacilityActionResult) => {
          if (result.success) {
            const logMessage =
              '-- Totals form saved with editmode ' +
              this.editMode.toString() +
              'on facility ' +
              this.facilityId.toString();
            this.logger.info(logMessage);
            this.facilitiesApiService.loadTotals(this.facilityId);
            this.resetTotalsForm();
            this.editing = false;
            this.savingForm = false;
            const config: NbIconConfig = {
              icon: 'alert-triangle-outline',
              pack: 'eva',
              status: 'success',
              options: {
                duration: 5000,
                destroyByClick: true,
              },
            };
            this.showToast(result.message, this.successMessage, config);
            this.formSaved.emit(true);
          } else {
            this.savingForm = false;
            this.editing = false;
            this.resetTotalsForm();
            const config: NbIconConfig = {
              icon: 'alert-triangle-outline',
              pack: 'eva',
              status: 'danger',
              options: {
                duration: 5000,
                destroyByClick: true,
              },
            };
            this.showToast(result.message, this.errorMessage, config);
            this.logger.error(result.message, result);
          }
        });
    }
  }

  private showToast(message: string, title: string, config: NbIconConfig): void {
    this.index += 1;
    this.toastrService.show(message, title, config);
  }

  private onFormSaved($e: string) {
    if ($e) {
      this.editing = false;
      this.trackingService.eventEmitter('totalsComponent', 'action', '', 'formSaved', 0);
    }
  }

  private setChartOptions(): void {
    this.chartOption = {
      series: [
        {
          name: 'totals',
          type: 'pie',
          selectedMode: 'single',
          clockwise: false,
          radius: ['70%', '85%'],
          center: ['50%', '50%'],
          avoidLabelOverlap: false,
          label: {
            show: false,
            position: 'center',
          },
          emphasis: {
            label: {
              show: false,
              fontFamily: 'InterRegularSemiBold',
              fontSize: '22',
              fontWeight: 'bold',
            },
          },
          labelLine: {
            show: false,
          },
          data: [
            {
              value: 180,
              itemStyle: {
                color: 'rgba(128,255,253,.24)',
              },
            },
            {
              value: 180,
              itemStyle: {
                color: 'rgba(12,206,107,.16)',
              },
            },
          ],
        },
        {
          name: 'totals',
          type: 'pie',
          animation: true,
          clockwise: true,
          radius: ['65%', '90%'],
          center: ['50%', '50%'],
          avoidLabelOverlap: true,
          label: {
            show: true,
            position: 'center',
            fontFamily: 'InterRegularSemiBold',
            fontSize: '22',
            fontWeight: 'bold',
            color: this.theme === 'parkbaseDark' ? '#e8f1f2' : '#142735',
            borderWidth: 0,
          },
          data: [],
        },
      ],
    };
  }
}
